import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { useCallback, useState } from 'react'

import { API_BASE } from '#api/client.js'
import { IconButton } from '#components/IconButton.js'

interface FetchDocumentsButtonProps {
  tenantRequestContent: Record<string, unknown>
}

const getDownloadName = (tenantRequestContent: Record<string, unknown>) => {
  const rawName =
    tenantRequestContent.requestId ||
    tenantRequestContent.externalId ||
    tenantRequestContent.trackingNumber ||
    'documents'

  return `documents-${String(rawName).replace(/[^a-z0-9-]+/gi, '-')}.zip`
}

const getErrorMessage = async (response: Response): Promise<string> => {
  try {
    const errorJson = await response.json()
    return errorJson.error || JSON.stringify(errorJson)
  } catch {
    return await response.text()
  }
}

export const FetchDocumentsButton = ({
  tenantRequestContent,
}: FetchDocumentsButtonProps) => {
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'fetched' | 'error'
  >('idle')

  const handleFetch = useCallback(async () => {
    setStatus('loading')
    try {
      const response = await fetch(`${API_BASE}/documents/fetch`, {
        method: 'POST',
        headers: {
          Accept: 'application/zip',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(tenantRequestContent),
      })

      if (!response.ok) {
        const errorBody = await getErrorMessage(response)
        throw new Error(
          `Failed to fetch documents: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`,
        )
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = getDownloadName(tenantRequestContent)
      link.click()
      URL.revokeObjectURL(url)

      setStatus('fetched')
      setTimeout(() => setStatus('idle'), 2000)
    } catch (error) {
      console.error(error)
      setStatus('error')
      setTimeout(() => setStatus('idle'), 3000)
    }
  }, [tenantRequestContent])

  const label =
    status === 'loading'
      ? 'Fetching...'
      : status === 'fetched'
        ? 'Downloaded!'
        : status === 'error'
          ? 'Failed'
          : 'Fetch Documents'

  return (
    <IconButton
      className="btn-link text-info"
      icon={ArrowDownTrayIcon}
      onClick={handleFetch}
      title={
        status === 'fetched'
          ? 'Downloaded!'
          : 'Fetch documents from files service'
      }
    >
      {label}
    </IconButton>
  )
}
